import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { GalleryLightbox } from "@/components/public/GalleryLightbox"
import { PublicMeta } from "@/components/public/PublicMeta"
import type { GalleryItemProps, GalleryOccasion, GalleryPageProps, PageProps } from "@/types/inertia"
import "@/styles/public-gallery.css"

const TITLE = "Personalised Wine Bottles Gallery"
const DESCRIPTION =
  "Personalised wine bottles we have made for clubs, couples and companies across the UK, each bespoke label designed around its own occasion."

// The gallery (F8, M7 decision 10): Duncan's pieces in his display order,
// filtered by occasion in the browser. The filter lives in the URL
// (?occasion=<slug>) and in the `selected_occasion` prop, which a client-side
// Inertia visit keeps in step, so the server-rendered page, the address bar
// and the chips always agree. A piece opens in the lightbox.

/** The current URL with ?occasion= set to `slug` (or removed); other parameters are kept. */
function withOccasion(url: string, slug: string | null) {
  const [path, query = ""] = url.split("?")
  const params = new URLSearchParams(query)
  if (slug) params.set("occasion", slug)
  else params.delete("occasion")
  const next = params.toString()
  return next ? `${path}?${next}` : path
}

function pieces(count: number) {
  return count === 1 ? "1 piece" : `${count} pieces`
}

export default function Gallery() {
  const { props, url } = usePage<PageProps<GalleryPageProps>>()
  const items = props.items ?? []
  const occasions = props.occasions ?? []
  const selected = props.selected_occasion ?? null
  const selectedOccasion = occasions.find((occasion) => occasion.value === selected) ?? null

  const visible = selected ? items.filter((item) => item.occasion === selected) : items
  const [openId, setOpenId] = React.useState<number | null>(null)
  const openers = React.useRef(new Map<number, HTMLButtonElement>())

  const chooseOccasion = (occasion: GalleryOccasion | null) => {
    setOpenId(null)
    router.replace({
      url: withOccasion(url, occasion?.slug ?? null),
      props: (current) => ({ ...current, selected_occasion: occasion?.value ?? null }),
      preserveState: true,
      preserveScroll: true,
    })
  }

  // On arrival, bring the address bar into line with the server's reading
  // of the filter: an unknown ?occasion= shows everything (so it is
  // dropped) and the underscored spelling becomes the slug. Chip changes
  // build the right URL themselves, so this runs once.
  const arrivalUrl = React.useRef(url)
  const arrivalSlug = React.useRef(selectedOccasion?.slug ?? null)
  React.useEffect(() => {
    const given = new URLSearchParams(arrivalUrl.current.split("?")[1] ?? "").get("occasion")
    if (given === arrivalSlug.current) return
    router.replace({
      url: withOccasion(arrivalUrl.current, arrivalSlug.current),
      preserveState: true,
      preserveScroll: true,
    })
  }, [])

  // Chips: every occasion with pieces, plus the chosen one even when empty.
  const chips = occasions.filter((occasion) => occasion.count > 0 || occasion.value === selected)

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="Our Work"
        title="Gallery"
        lede="Bespoke bottles created for clubs, couples and companies across the UK."
        actions={
          <Button asChild variant="gold" size="lg">
            <Link href="/contact">Request a Consultation</Link>
          </Button>
        }
      />

      <section className="band band-charcoal gal-band" aria-labelledby="gallery-pieces-title">
        <div className="shell">
          <h2 id="gallery-pieces-title" className="sr-only">
            Pieces in the gallery
          </h2>

          {items.length > 0 && (
            <div className="gal-filters">
              <div className="gal-chips" role="group" aria-label="Show pieces for an occasion">
                <button
                  type="button"
                  className="gal-chip"
                  aria-pressed={selected === null}
                  onClick={() => chooseOccasion(null)}
                >
                  All <span className="gal-chip-count">{items.length}</span>
                </button>
                {chips.map((occasion) => (
                  <button
                    key={occasion.value}
                    type="button"
                    className="gal-chip"
                    aria-pressed={selected === occasion.value}
                    onClick={() => chooseOccasion(occasion)}
                  >
                    {occasion.label} <span className="gal-chip-count">{occasion.count}</span>
                  </button>
                ))}
              </div>
              <div className="gal-count" role="status">
                {selectedOccasion
                  ? `${pieces(visible.length)} · ${selectedOccasion.label}`
                  : pieces(visible.length)}
              </div>
            </div>
          )}

          {visible.length > 0 ? (
            <ul className="gal-grid">
              {visible.map((item) => (
                <li key={item.id}>
                  <GalleryCard
                    item={item}
                    onOpen={() => setOpenId(item.id)}
                    buttonRef={(element) => {
                      if (element) openers.current.set(item.id, element)
                      else openers.current.delete(item.id)
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : items.length > 0 && selectedOccasion ? (
            <div className="gal-empty">
              <h2>No {selectedOccasion.label} pieces yet</h2>
              <p>
                We have not added bottles for {selectedOccasion.label.toLowerCase()} to the gallery yet.
                See everything else, or design your own label in our studio.
              </p>
              <div className="gal-empty-actions">
                <Button type="button" variant="ghost-hairline" size="lg" onClick={() => chooseOccasion(null)}>
                  Show all pieces
                </Button>
                <Button asChild variant="gold" size="lg">
                  <Link href="/studio">Design Your Own</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="gal-empty">
              <h2>No pieces to show yet</h2>
              <p>
                Bottles we have made will appear here. In the meantime, design your own label in our
                studio or tell us about your occasion.
              </p>
              <div className="gal-empty-actions">
                <Button asChild variant="gold" size="lg">
                  <Link href="/studio">Design Your Own</Link>
                </Button>
                <Button asChild variant="ghost-hairline" size="lg">
                  <Link href="/contact">Request a Consultation</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <GalleryLightbox
        items={visible}
        openId={openId}
        onOpenIdChange={setOpenId}
        onClosed={(id) => openers.current.get(id)?.focus()}
      />

      <ConsultationBand
        eyebrow="Your turn"
        title="Design your own"
        body="Start from a template in our label studio or bring your own artwork, and see your label on the bottle before you enquire."
        actionLabel="Design Your Own"
        actionHref="/studio"
        brochure
      />
    </PublicShell>
  )
}

function GalleryCard({
  item,
  onOpen,
  buttonRef,
}: {
  item: GalleryItemProps
  onOpen: () => void
  buttonRef: (element: HTMLButtonElement | null) => void
}) {
  // Both renditions keep the photograph's proportions (600 and 1200 px on
  // the long edge), so the 1200 one is twice the 600's width.
  const srcSet = item.width
    ? `${item.thumb_url} ${Math.round(item.width / 2)}w, ${item.image_url} ${item.width}w`
    : undefined

  return (
    <article className="gal-card">
      <button
        ref={buttonRef}
        type="button"
        className="gal-open"
        aria-haspopup="dialog"
        aria-label={`View ${item.title} larger`}
        onClick={onOpen}
      >
        <span className="gal-frame">
          <img
            src={item.thumb_url}
            srcSet={srcSet}
            sizes="(min-width: 1180px) 344px, (min-width: 1024px) 31vw, (min-width: 640px) 46vw, 92vw"
            alt={item.title}
            width={item.width ? Math.round(item.width / 2) : undefined}
            height={item.height ? Math.round(item.height / 2) : undefined}
            loading="lazy"
            decoding="async"
          />
        </span>
      </button>
      <div className="gal-meta">
        <span className="gal-occasion">{item.occasion_label}</span>
        <h3 className="gal-title">{item.title}</h3>
        {item.caption && <p className="gal-caption">{item.caption}</p>}
      </div>
    </article>
  )
}
